/**
 * Adults only — hand a kid real money out of a chosen bucket. Writes a negative
 * ledger row via the Worker, which refuses to overdraw the bucket. A failed payout
 * keeps the amount on screen.
 */
import { useState, type FormEvent } from 'react';
import { api, ApiRequestError, money, parseMoney } from '../../lib/api';
import type { KidWallet, MemberSummary, MoneyBucket } from '../../shared/types';
import {
  BUCKETS,
  Modal,
  bucketMeta,
  btnPrimary,
  btnSecondary,
  cx,
  fieldInput,
  fieldLabel,
} from './shared';

export default function PayoutForm({
  member,
  wallet,
  onClose,
  onSaved,
}: {
  member: MemberSummary;
  wallet: KidWallet;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [bucket, setBucket] = useState<MoneyBucket>('spend');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const bucketBalance = wallet[bucket];

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const cents = parseMoney(amount);
    if (cents === null || cents <= 0) {
      setError('Enter an amount, like 12.50.');
      return;
    }
    if (cents > bucketBalance) {
      setError(`${member.name}'s ${bucketMeta(bucket).label} bucket only has ${money(bucketBalance)}.`);
      return;
    }

    setSaving(true);
    try {
      await api.post('/kidmoney/payout', {
        member_id: member.id,
        bucket,
        amount: cents,
        note: note.trim() || undefined,
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiRequestError ? err.message : 'That did not save. Try again in a second.'
      );
      setSaving(false);
    }
  }

  return (
    <Modal title={`Pay ${member.name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="payout-bucket" className={fieldLabel}>
            Out of which bucket
          </label>
          <select
            id="payout-bucket"
            value={bucket}
            onChange={(ev) => setBucket(ev.target.value as MoneyBucket)}
            className={fieldInput}
          >
            {BUCKETS.map((b) => (
              <option key={b.key} value={b.key}>
                {b.emoji} {b.label} — {money(wallet[b.key])}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="payout-amount" className={fieldLabel}>
            Amount to hand over
          </label>
          <div className="relative">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-faint"
            >
              $
            </span>
            <input
              id="payout-amount"
              value={amount}
              onChange={(ev) => setAmount(ev.target.value)}
              className={cx(fieldInput, 'pl-7')}
              inputMode="decimal"
              placeholder="12.50"
              autoFocus
              required
            />
          </div>
          <button
            type="button"
            onClick={() => setAmount((bucketBalance / 100).toFixed(2))}
            disabled={bucketBalance <= 0}
            className="mt-2 text-sm font-medium text-sage underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:opacity-50"
          >
            Pay all {money(bucketBalance)}
          </button>
        </div>

        <div>
          <label htmlFor="payout-note" className={fieldLabel}>
            Note <span className="font-normal text-ink-faint">(optional)</span>
          </label>
          <input
            id="payout-note"
            value={note}
            onChange={(ev) => setNote(ev.target.value)}
            className={fieldInput}
            placeholder="Cash for the school trip"
            maxLength={80}
          />
        </div>

        {error ? (
          <p role="alert" className="rounded-xl bg-clay-soft px-3 py-2 text-sm text-clay">
            {error}
          </p>
        ) : null}

        <div className="flex gap-2 pt-1">
          <button
            type="submit"
            disabled={saving || bucketBalance <= 0}
            className={cx(btnPrimary, 'flex-1')}
          >
            {saving ? 'Saving…' : 'Record payout'}
          </button>
          <button type="button" onClick={onClose} className={btnSecondary}>
            Cancel
          </button>
        </div>
        <p className="text-xs text-ink-faint">
          This records that you handed the money over. It comes straight off that bucket.
        </p>
      </form>
    </Modal>
  );
}
